import {
  ActivityIndicator,
  Pressable,
  type PressableProps,
  type PressableStateCallbackType,
  StyleSheet,
  type StyleProp,
  Text,
  View,
  type ViewStyle,
} from 'react-native';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

type ButtonVariant = 'primary' | 'secondary' | 'danger';

type ButtonProps = PressableProps & {
  title: string;
  variant?: ButtonVariant;
  loading?: boolean;
  loadingTitle?: string;
  style?: StyleProp<ViewStyle>;
};

export function Button({
  title,
  variant = 'primary',
  loading = false,
  loadingTitle,
  disabled,
  style,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const textStyle = StyleSheet.flatten([
    styles.text,
    variant === 'secondary' ? styles.secondaryText : null,
  ]);

  function resolveStyle(state: PressableStateCallbackType) {
    const callerStyle =
      typeof style === 'function'
        ? style(state)
        : style;

    return StyleSheet.flatten([
      styles.base,
      styles[variant],
      state.pressed && !isDisabled ? styles[`${variant}Pressed`] : null,
      isDisabled ? styles.disabled : null,
      callerStyle,
    ]) as ViewStyle;
  }

  return (
    <Pressable
      accessibilityRole="button"
      disabled={isDisabled}
      style={resolveStyle}
      {...props}
    >
      {loading && loadingTitle ? (
        <View style={styles.loadingContent}>
          <ActivityIndicator
            color={variant === 'secondary' ? colors.text : colors.surface}
            size="small"
          />
          <Text
            style={textStyle}
          >
            {loadingTitle}
          </Text>
        </View>
      ) : loading ? (
        <ActivityIndicator
          color={variant === 'secondary' ? colors.text : colors.surface}
        />
      ) : (
        <Text style={textStyle}>
          {title}
        </Text>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    minHeight: 48,
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  primary: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  primaryPressed: {
    backgroundColor: colors.primaryPressed,
    borderColor: colors.primaryPressed,
  },
  secondary: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.secondary,
  },
  secondaryPressed: {
    backgroundColor: colors.surfacePressed,
  },
  danger: {
    backgroundColor: colors.danger,
    borderColor: colors.danger,
  },
  dangerPressed: {
    backgroundColor: colors.dangerPressed,
    borderColor: colors.dangerPressed,
  },
  disabled: {
    opacity: 0.6,
  },
  text: {
    color: colors.surface,
    fontSize: 16,
    fontWeight: '700',
  },
  loadingContent: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    justifyContent: 'center',
  },
  secondaryText: {
    color: colors.secondary,
  },
});
